import React, { Suspense, lazy, useRef, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

const RecordingManualAudio = lazy(() => import("recordingManualAudio/App"));

function getStatusValue(event) {
  if (typeof event === "string") return event;
  if (event && typeof event === "object" && typeof event.status === "string") {
    return event.status;
  }
  return null;
}

function getUploadId(event) {
  if (!event || typeof event !== "object") return null;

  const candidate =
    event.uploadId ?? event.recordingId ?? event.docid ?? event.docId ?? null;

  if (candidate == null) return null;
  const normalized = String(candidate).trim();
  return normalized || null;
}

export default function RecordingManualAudioRouter(props) {
  const navigate = useNavigate();
  const didRedirectRef = useRef(false);

  const redirectToResult = useCallback(
    ({ recordingId, variant }) => {
      if (!recordingId) {
        navigate("/results?tab=manual-audio", { replace: true });
        return;
      }

      const params = new URLSearchParams({
        variant: variant || "RecordingManualAudio",
        docid: recordingId,
        context: "manual-audio",
      });

      navigate(`/result?${params.toString()}`, { replace: true });
    },
    [navigate],
  );

  const handleUploadStatus = useCallback(
    (nextStatus) => {
      props.onUploadStatus?.(nextStatus);

      if (didRedirectRef.current) return;

      const statusValue = getStatusValue(nextStatus);
      const uploadId = getUploadId(nextStatus);
      if (statusValue !== "uploading" || !uploadId) return;

      didRedirectRef.current = true;
      redirectToResult({
        recordingId: uploadId,
        variant:
          nextStatus?.variant || props.variant || "RecordingManualAudio",
      });
    },
    [props, redirectToResult],
  );

  const handleComplete = useCallback(
    (payload) => {
      props.onComplete?.(payload);
      if (didRedirectRef.current) return;

      didRedirectRef.current = true;
      redirectToResult({
        recordingId: payload?.recordingId || null,
        variant: payload?.variant || props.variant || "RecordingManualAudio",
      });
    },
    [props, redirectToResult],
  );

  return (
    <Suspense
      fallback={
        <div className="flex h-full w-full items-center justify-center">
          <ClipLoader size={48} />
        </div>
      }
    >
      <RecordingManualAudio
        {...props}
        onComplete={handleComplete}
        onUploadStatus={handleUploadStatus}
        onUploadProgress={props.onUploadProgress}
      />
    </Suspense>
  );
}
